import { getShapes } from 'ws-app';

const UnityCheckType = {
  NONE: 'none',
  BENDING: 'bending',
  SHEAR: 'shear',
  DEFLECTION: 'deflection'
};

const northAmericanSteelShapes = {
  'W': 'AISC/W-shapes.json',
  'M': 'AISC/M-shapes.json',
  'S': 'AISC/S-shapes.json',
  'HP': 'AISC/HP-shapes.json',
  'C': 'AISC/C-shapes.json',
  'MC': 'AISC/MC-shapes.json',
  'WT': 'AISC/WT-shapes.json',
  'MT': 'AISC/MT-shapes.json',
  'ST': 'AISC/ST-shapes.json',
  'L': 'AISC/L-shapes.json',
  '2L': 'AISC/2L-shapes.json',
  'HSS Square': 'AISC/HSS-Square-shapes.json',
  'HSS Rectangle': 'AISC/HSS-Rectangle-shapes.json', 
  'HSS Round': 'AISC/HSS-Round-shapes.json',
  'PIPE': 'AISC/PIPE-shapes.json'
}

const engineeredWoodShapes = {
  'TimberStrand LSL': 'engineered-lumber/trus-joist-shapes.json',
  'Parallam PSL': 'engineered-lumber/trus-joist-shapes.json',
  'Stud LVL (East)': 'engineered-lumber/other-vendors-east-shapes.json',
  'LVL (East)': 'engineered-lumber/other-vendors-east-shapes.json',
  'LVL (West)': 'engineered-lumber/other-vendors-west-shapes.json',
  'Glulam 24F-V4 IJC': 'engineered-lumber/boise-cascade-glulam-shapes.json'
}

// Helper function to determine which unity check controls
function getControllingUnityCheck(results) {
  let bendingUnity = results.bending_checks.controlling_unity
  let shearUnity = results.shear_checks.controlling_unity
  let deflectionUnity = results.deflection_checks.controlling_unity
  // Collect all failing checks
  const failing = [
    { type: UnityCheckType.BENDING, value: bendingUnity },
    { type: UnityCheckType.SHEAR, value: shearUnity },
    { type: UnityCheckType.DEFLECTION, value: deflectionUnity }
  ].filter(check => check.value > 1.0);

  if (failing.length === 0) return UnityCheckType.NONE;

  // Return the type of the check with the largest value
  return failing.reduce((max, curr) => curr.value > max.value ? curr : max);
}

function shapeSuggestionSupported(project) {
  switch(project.shapeCategory) {
    case 'north-american-steel':
      return true;
    case 'dimensional-lumber':
    case 'engineered-wood':
      return !project.shape.flitch;
    default:
      return false;
  }
}

function findShapeWithIx(project, shapes, requiredIx) {
  // Find the first shape with Ix >= requiredIx
  // TODO: check the following If the shape is a HSS, and the rotation is not 0, use the Iy
  let betterShape;
  
  if (project.shapeCategory === 'north-american-steel' && project.beam.crossSectionRotation !== 0) {
    betterShape = shapes.find(s => parseFloat(s.Iy) >= parseFloat(requiredIx));
  } else {
    betterShape = shapes.find(s => parseFloat(s.Ix) >= parseFloat(requiredIx));
  }
  
  if (betterShape) {
    // You can update the UI or return this shape as needed
    return betterShape;
  } else {
    console.log("No suitable shape found with required I.");
  }
}

function findShapeWithA(shapes, requiredA) {
  // Find the first shape with A >= requiredA
  const betterShape = shapes.find(s => parseFloat(s.A) >= requiredA);

  if (betterShape) {
    // You can update the UI or return this shape as needed
    return betterShape;
  } else {
    console.log("No suitable shape found with required A.");
  }
}

function calculateRequiredIx(project, unity) {
  let requiredIx = unity * parseFloat(project.shape.Ix);
  
  // if hss and rotation not 0, use the Iy
  if (project.shapeCategory === 'north-american-steel' && project.beam.crossSectionRotation !== 0) {
    return unity * parseFloat(project.shape.Iy);
  }

  return requiredIx;
}

function calculateRequiredA(project, unity) {
  return unity * parseFloat(project.shape.A);
}


export async function suggestShape(project, results) {
  // Check if shape suggestion is supported for the project
  if(!shapeSuggestionSupported(project)) {
    console.log("Shape suggestion not supported for this project.");
    return;
  }

  const controllingUnity = getControllingUnityCheck(results);

  if(controllingUnity.type === UnityCheckType.NONE) {
    console.log("All unity checks are OK");
    return;
  }

  let filename = ''

  if(project.shapeCategory === 'north-american-steel') {
    filename = northAmericanSteelShapes[project.shapeSubCategory]
  } else if(project.shapeCategory === 'dimensional-lumber') {
    filename = `Wood/Lumber-single-ply.json`
  } else if(project.shapeCategory === 'engineered-wood') {
    filename = engineeredWoodShapes[project.shapeSubCategory]
  }

  let shapes;
  try {
    shapes = await getShapes(filename);
  } catch (error) {
    console.error(`Failed to fetch shapes from file ${filename}:`, error);
    return;
  }

  if (controllingUnity.type === UnityCheckType.BENDING || controllingUnity.type === UnityCheckType.DEFLECTION) {  
    const requiredIx = calculateRequiredIx(project, controllingUnity.value);
    return findShapeWithIx(project, shapes, requiredIx);

  } else if (controllingUnity.type === UnityCheckType.SHEAR) {
    const requiredA = calculateRequiredA(project, controllingUnity.value);
    return findShapeWithA(shapes, requiredA);
  } 

}

export function shapeName(shape) {

  if('AISC_Manual_Label' in shape) {
    return shape['AISC_Manual_Label']
  }

  if('Profile' in shape) {
    return shape['Profile'];
  }

  if('Grade' in shape) {
    let str = shape['Type'] + " " + shape['Grade'] + " " + shape['Width'] + " &times; " + shape['Height']
    if(shape.flitch) {
      str += ` Flitch`
    }
    return str
  }
  
  if('Name' in shape) {
    let str = shape['Name']
    if(shape.flitch) {
      str += ` Flitch`
    }
    return str
  }
        
}
